import {ChatBubbleBottomCenterTextIcon, ExclamationCircleIcon} from "@heroicons/react/20/solid";
import {MessageType, selectableMessageTypes, toMessageType} from "@ots-frontend/ots-data";
import {KeyboardEvent, ReactNode, useEffect, useId, useMemo, useRef, useState} from "react";
import {OtsFormDialog} from "../components/OtsFormDialog";
import {
  otsInputClassName,
  otsInputInvalidBorderClassName,
  otsInputValidBorderClassName
} from "../components/OtsPasswordInput";
import {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";

/** What goes with the text: how the comment is filed. */
export type CommentDialogDetails = {
  /** The message type's database code, e.g. "Comment". */
  messageType: string;
};

export type CommentDialogProps = {
  open: boolean;
  /** What was typed, or null when it was left blank -- which only happens when `required` is off. */
  onSubmit: (comment: string | null, details: CommentDialogDetails) => void;
  /**
   * Cancel, Escape, or a click outside. Some hosts carry on without a comment when this fires (a
   * status change still saves), so every way out of the dialog lands here.
   */
  onCancel: () => void;
  title?: string;
  description?: string;
  /** Text already on the record, when editing one. */
  initialValue?: string;
  /** Names the field for screen readers; the title already says what to write, so it is not shown. */
  label?: string;
  placeholder?: string;
  submitLabel?: string;
  /** Refuses a blank submit and says why, rather than submitting null. */
  required?: boolean;
  requiredMessage?: string;
  maxLength?: number;
  /** A failed save from the host. The dialog stays open so the comment can be retried. */
  error?: ReactNode;
  /** Disables dismissal and presents progress while the host saves the comment. */
  isSubmitting?: boolean;
  /**
   * The message type's database code. Defaults to Comment; a code the server does not know reads
   * as Comment, as the server reads it.
   */
  messageType?: string;
  /** Shows a type picker. Off by default, so the comment is filed as `messageType`. */
  allowMessageTypeChange?: boolean;
  /**
   * The types the picker offers. Defaults to the ones anyone may choose; a host that knows the
   * person may use restricted types (Internal) passes them in.
   */
  messageTypes?: MessageType[];
  brand?: OtsBrand;
  /**
   * Light or dark. Defaults to "system" -- the host page's theme, else the OS -- for a dialog opened
   * on its own. A page opening it from inside its own theme root passes "inherit" to match itself.
   */
  colorScheme?: OtsColorSchemePreference;
};

const fieldLabelClassName = "block text-sm font-medium text-[var(--ots-text)]";

/** Asks for a free-text comment: a dispute explanation, a status-change remark, a completion note. */
export const CommentDialog = ({
  open,
  onSubmit,
  onCancel,
  title = "Comments",
  description,
  initialValue,
  label = "Comment",
  placeholder,
  submitLabel = "Submit",
  required = false,
  requiredMessage = "Enter a comment to continue.",
  maxLength,
  error: submitError,
  isSubmitting = false,
  messageType,
  allowMessageTypeChange = false,
  messageTypes = selectableMessageTypes,
  brand,
  colorScheme
}: CommentDialogProps) => {
  const initialType = toMessageType(messageType).code;

  const [value, setValue] = useState(initialValue ?? "");
  const [type, setType] = useState(initialType);
  const [attempted, setAttempted] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const id = useId();
  const textareaId = `ots-comment-${id}`;
  const typeId = `ots-comment-type-${id}`;

  // Every opening starts from the record as it is; nothing chosen or typed last time carries over.
  useEffect(() => {
    if (open) {
      setValue(initialValue ?? "");
      setType(initialType);
      setAttempted(false);
    }
  }, [open, initialValue, initialType]);

  // A record already filed under a type the picker does not offer -- including a restricted one this
  // person may not choose -- keeps that type as an option, so opening it does not quietly refile it.
  const typeOptions = useMemo(
    () => messageTypes.some((option) => option.code === initialType)
      ? messageTypes
      : [toMessageType(initialType), ...messageTypes],
    [messageTypes, initialType]
  );

  const blank = value.trim() === "";
  // Quiet until the first submit, then it follows what is typed, so the message goes as soon as
  // there is something to send.
  const error = required && attempted && blank ? requiredMessage : undefined;
  const errorId = error ? `${textareaId}-error` : undefined;

  const submit = () => {
    if (isSubmitting) return;
    if (required && blank) {
      setAttempted(true);
      textarea.current?.focus();
      return;
    }

    onSubmit(blank ? null : value, {messageType: type});
  };

  // Enter starts a new line, since a note often runs to several; Ctrl/Cmd+Enter sends it.
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <OtsFormDialog
      open={open}
      onClose={onCancel}
      onSubmit={submit}
      title={title}
      description={description}
      icon={<ChatBubbleBottomCenterTextIcon/>}
      formLabel={typeof title === "string" ? title : "Comment"}
      error={submitError}
      submitLabel={submitLabel}
      submittingLabel="Adding comment…"
      progressLabel="Adding comment"
      submitting={isSubmitting}
      size="lg"
      brand={brand}
      colorScheme={colorScheme}>
      {allowMessageTypeChange && (
        <div>
          <label htmlFor={typeId} className={fieldLabelClassName}>Type</label>
          <select
            id={typeId}
            name="messageType"
            value={type}
            onChange={(event) => setType(event.target.value)}
            className={[otsInputClassName, otsInputValidBorderClassName, "mt-1.5"].join(" ")}>
            {/* Codes are the server's own readable names, so they double as labels. */}
            {typeOptions.map((option) => (
              <option key={option.code} value={option.code}>{option.code}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label htmlFor={textareaId} className={allowMessageTypeChange ? `${fieldLabelClassName} mb-1.5` : "sr-only"}>
          {label}
        </label>
        <textarea
          ref={textarea}
          id={textareaId}
          name="comment"
          rows={5}
          autoFocus
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          className={[
            otsInputClassName,
            "min-h-28 resize-y",
            error ? otsInputInvalidBorderClassName : otsInputValidBorderClassName
          ].join(" ")}/>

        <div className="mt-1.5 flex items-start justify-between gap-3">
          {error ? (
            <p id={errorId} className="flex items-start gap-1 text-xs font-medium text-[var(--ots-error)]">
              <ExclamationCircleIcon aria-hidden="true" className="mt-px size-3.5 shrink-0"/>
              <span>{error}</span>
            </p>
          ) : <span/>}
          {maxLength !== undefined && (
            <span className="shrink-0 text-xs tabular-nums text-[var(--ots-text-muted)]">
              {value.length} / {maxLength}
            </span>
          )}
        </div>
      </div>
    </OtsFormDialog>
  );
};
